import { Component } from '@theme/component';
import { mediaQueryLarge, prefersReducedMotion } from '@theme/utilities';

/**
 * Mobile card-deck swipe + synced product carousel for the Collection reel widget.
 *
 * Desktop stays a plain, JS-free vertical gallery (CSS only, next to the sticky main
 * image); below the 768px breakpoint the same `_collection-reel-image` blocks become a
 * swipeable stack — same mechanism as `assets/product-editorial.js` (every card shares
 * one grid cell via `grid-area: 1 / 1`, see the section's own stylesheet; this file only
 * tracks which one is "active" and animates the move to a neighbor), swiping both ways
 * just like that file: a leftward drag advances (`#advance`) and a rightward drag
 * retreats (`#retreat`) to the previous card, playing the exact same flight in reverse.
 * See that file's own doc comment for the forward exit-animation/z-index choreography
 * this mirrors; the backward one is its own doc comment on `#animateRetreatTo`.
 *
 * On top of that, this component keeps the product carousel underneath the stack in
 * sync, both ways:
 *  - a stack swipe (either direction) calls `select()` on the carousel to match;
 *  - a user-initiated change on the carousel (a drag to an adjacent slide) plays the
 *    matching advance/retreat animation on the stack too, so the two always look
 *    identical; an arbitrary jump (anything not adjacent) just snaps the stack to that
 *    card with no flourish.
 * The carousel's own slides render empty in Liquid (sections/collection-reel.liquid) —
 * `#moveCardsIntoCarousel()` fills them by moving each tile's already-rendered hover card
 * over, rather than having the section render the same product a second time itself: a
 * block's `product` setting only resolves into a real product inside that block's own
 * render, not when read back from `section.blocks`, so a second Liquid render from the
 * section silently produced blank/placeholder cards.
 */

// Every card in the deck stays visible (not just the top few), each one peeking
// STACK_DEPTH_OFFSET_PX above the card in front of it — a constant per-card step
// matching the stack's own dynamic padding-top (`section.blocks.size * 20px`, set
// inline by sections/collection-reel.liquid), so the deepest card's peek always lines up
// with the reserved space above the stack. Cards further back also scale down a little
// per depth (STACK_DEPTH_SCALE_STEP, floored at STACK_MIN_SCALE) for a subtle receding
// effect — see restStyleForDepth for why translateY alone isn't the true peek height once
// scale is involved.
const STACK_DEPTH_OFFSET_PX = 20;
const STACK_DEPTH_SCALE_STEP = 0.05;
const STACK_MIN_SCALE = 0.7;
// Must match the animation's own duration and 55% checkpoint in the
// `collection-reel-image-exit` keyframes (section stylesheet).
const TRANSITION_MS = 550;
const EXIT_REVERSAL_FRACTION = 0.55;
// Minimum deliberate horizontal drag, in px, to count as an advance/retreat swipe
// rather than an incidental tap or a vertical page scroll.
const SWIPE_THRESHOLD_PX = 40;
// Any movement below this, in px (either axis), still counts as a stationary tap —
// accounts for natural finger/mouse jitter, same tolerance as product-editorial.js.
const TAP_MOVE_TOLERANCE_PX = 10;
const DIRECTION_LOCK_MOVE_PX = TAP_MOVE_TOLERANCE_PX;
// Delay, in ms, before the one-shot "swipeable" hint plays after the stack's
// first render (`#playSwipeHint`) — long enough for assets/motion.js's own
// entrance fade/rise (`--motion-reveal-duration`, motion.css) and this file's
// own resting `transition` to settle first, so the hint never competes with
// either for the same `transform` property.
const SWIPE_HINT_DELAY_MS = 1200;

/**
 * Pure function: given how far behind the active card a tile is (how many cards are in
 * the deck, and their shared rendered height), return the resting transform/z-index for
 * that depth. Never includes rotation — the resting stack is always perfectly straight,
 * offset only vertically. Opacity is always 1: every card in the deck stays visible.
 *
 * `scale()` uses `transform-origin: center` (the default) — shrinking a card moves its
 * top edge DOWN by `(1 - scale) * cardHeight / 2` at the same time translateY tries to
 * move it up, so a naive `-depth * STACK_DEPTH_OFFSET_PX` translate alone would leave a
 * thinner peek at every depth than intended (worse the more it's scaled down). Baking
 * that shift into `y` here keeps the actual visible peek an exact, uniform
 * STACK_DEPTH_OFFSET_PX at every depth regardless of how much smaller the card gets.
 * @param {number} depth
 * @param {number} total
 * @param {number} cardHeight - Rendered height of a stack card (`offsetHeight` — layout
 * size, unaffected by any transform already applied to it).
 */
function restStyleForDepth(depth, total, cardHeight) {
  const scale = Math.max(STACK_MIN_SCALE, 1 - depth * STACK_DEPTH_SCALE_STEP);
  const scaleShift = ((1 - scale) * cardHeight) / 2;
  const y = -depth * STACK_DEPTH_OFFSET_PX - scaleShift;
  const zIndex = total - depth;
  return { y, scale, opacity: 1, zIndex };
}

/**
 * @param {HTMLElement} cardEl
 * @param {number} depth
 * @param {number} total
 * @param {number} cardHeight
 */
function applyRest(cardEl, depth, total, cardHeight) {
  const { y, scale, opacity, zIndex } = restStyleForDepth(depth, total, cardHeight);
  cardEl.style.transform = `translate(0px, ${y}px) rotate(0deg) scale(${scale})`;
  cardEl.style.opacity = String(opacity);
  cardEl.style.zIndex = String(zIndex);
}

/**
 * Every stack depth stays visually present, so a tile's own `<video autoplay loop>`
 * would otherwise decode and loop forever while obscured behind the active card. Only
 * the active (depth 0) tile's video plays.
 * @param {HTMLElement} cardEl
 * @param {number} depth
 */
function syncVideoPlayback(cardEl, depth) {
  const video = /** @type {HTMLVideoElement | null} */ (cardEl.querySelector('video'));
  if (!video) return;

  if (depth === 0 && !prefersReducedMotion()) {
    video.play();
  } else {
    video.pause();
  }
}

/**
 * @param {number} cardIndex
 * @param {number} activeIndex
 * @param {number} total
 */
function depthOf(cardIndex, activeIndex, total) {
  return (((cardIndex - activeIndex) % total) + total) % total;
}

export class CollectionReelComponent extends Component {
  /** @type {HTMLElement[]} */
  #cards = [];
  /** Slideshow custom element (assets/slideshow.js) driving the carousel underneath. */
  /** @type {any} */
  #carousel = null;
  // `.collection-reel__gallery` — the stack only, not `this` (the whole component, which
  // also wraps the carousel as a sibling). Pointer/touch listeners bind here specifically
  // so a drag on the carousel underneath never bubbles into the stack's own gesture
  // handling. Cached so `#destroy()` can remove the same listeners it added.
  /** @type {HTMLElement | null} */
  #wrapper = null;
  #activeIndex = 0;
  #isAnimating = false;
  #pointerDownX = 0;
  #pointerDownY = 0;
  // Invalidates in-flight animation cleanup once torn down (breakpoint flipping back to
  // desktop mid-exit), so it never touches a card this instance no longer owns.
  #generation = 0;
  // `null` until `DIRECTION_LOCK_MOVE_PX` of total movement has happened in the current
  // gesture, then locked to whichever axis dominates — decides both whether the drag
  // counts as an advance/retreat swipe (`#handlePointerUp`) and whether it should cancel
  // the section's native `touch-action: pan-y` scroll (`#handleTouchMove`).
  /** @type {'horizontal' | 'vertical' | null} */
  #dragDirection = null;
  // Set at pointerdown when a card animation is still in flight, so the rest of that
  // gesture is ignored outright instead of relying only on #advance()/#retreat()'s own
  // #isAnimating no-op.
  #ignoringGesture = false;
  // Watches the first card for assets/motion.js's one-shot `motion-revealed` class when
  // `#init()` has to defer `#render()` until that fires — see `#renderOnceRevealed`. Torn
  // down early if `#destroy()` runs first (a breakpoint flip back to desktop before the
  // group ever scrolls into view), so it can't fire `#render()` against an already-cleared
  // deck.
  /** @type {MutationObserver | null} */
  #revealObserver = null;

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
    this.#handleBreakpointChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
    this.#destroy();
  }

  #handleBreakpointChange = () => {
    if (mediaQueryLarge.matches) {
      this.#destroy();
    } else {
      this.#init();
    }
  };

  #init() {
    if (this.#cards.length) return;

    const wrapper = /** @type {HTMLElement | null} */ (this.querySelector('.collection-reel__gallery'));
    if (!wrapper) return;

    // Not `wrapper.children` — each block can render its own sibling `<style>` tag (e.g.
    // a per-block contrast-override), so the wrapper's direct children aren't only tiles.
    this.#cards = /** @type {HTMLElement[]} */ (
      Array.from(wrapper.querySelectorAll(':scope > .collection-reel-image'))
    );

    this.#carousel = /** @type {any} */ (this.querySelector('slideshow-component'));

    if (this.#cards.length < 2) return; // nothing to cycle

    this.#wrapper = wrapper;
    this.#activeIndex = 0;
    this.#isAnimating = false;
    this.#renderOnceRevealed(wrapper);
    this.#moveCardsIntoCarousel();

    // Scoped to the gallery, not `this` (the whole component) — `this` also wraps the
    // synced carousel as a sibling, and a drag on it would otherwise bubble into these
    // same handlers and get double-counted as a stack swipe on top of whatever
    // #handleCarouselSelect already did.
    wrapper.addEventListener('pointerdown', this.#handlePointerDown);
    wrapper.addEventListener('pointermove', this.#handlePointerMove);
    wrapper.addEventListener('pointerup', this.#handlePointerUp);
    wrapper.addEventListener('pointercancel', this.#handlePointerCancel);
    // `passive: false` so `preventDefault()` can actually cancel the browser's native
    // scroll — a real `touchmove` listener, not `pointermove` (see
    // `#handleTouchMove`'s own comment, mirrors product-editorial.js).
    wrapper.addEventListener('touchmove', this.#handleTouchMove, { passive: false });

    this.#withCarousel((carousel) => {
      carousel.addEventListener('slideshow:select', this.#handleCarouselSelect);
      carousel.select(0, undefined, { animate: false });
    });
  }

  /**
   * Physically moves each tile's `.collection-reel-image__card` (blocks/
   * _collection-reel-image.liquid's hover card — already rendered with the real product,
   * resolved correctly there) into the matching empty carousel slide
   * (sections/collection-reel.liquid renders those slides empty; a block's `product`
   * setting only resolves into a real product inside that block's own render, not when
   * the section reads `section.blocks` itself — see both files' doc comments). Matched
   * positionally: both the tiles and the slides come from the same `section.blocks`
   * order, so index `i` in one always corresponds to index `i` in the other. Plain DOM
   * moves, not gated behind `#withCarousel` — they don't touch the slideshow's own API,
   * so they work whether or not `<slideshow-component>` has upgraded yet.
   */
  #moveCardsIntoCarousel() {
    if (!this.#carousel) return;
    const slides = this.#carousel.querySelectorAll('slideshow-slide');

    this.#cards.forEach((tile, i) => {
      const card = tile.querySelector(':scope > .collection-reel-image__card');
      const slide = slides[i];
      if (card && slide) slide.append(card);
    });
  }

  /**
   * Reverses `#moveCardsIntoCarousel()` on the way back to desktop — each card returns to
   * its own tile, where the hover-card CSS expects it to live.
   */
  #restoreCardsFromCarousel() {
    if (!this.#carousel) return;
    const slides = this.#carousel.querySelectorAll('slideshow-slide');

    this.#cards.forEach((tile, i) => {
      const card = slides[i]?.querySelector(':scope > .collection-reel-image__card');
      if (card) tile.append(card);
    });
  }

  /**
   * This component's own script (embedded in the section) runs before
   * `assets/slideshow.js` — loaded globally, later in the document, by
   * `snippets/scripts.liquid` — has necessarily executed, so `<slideshow-component>`
   * may still be an unupgraded plain element the instant `#init()` runs (no `.select`
   * method yet). Runs `callback` once the class is actually registered — immediately if
   * it already is, matching how every other caller in this file expects `#carousel` to
   * behave.
   * @param {(carousel: any) => void} callback
   */
  #withCarousel(callback) {
    const carousel = this.#carousel;
    if (!carousel) return;

    if (typeof carousel.select === 'function') {
      callback(carousel);
      return;
    }

    customElements.whenDefined('slideshow-component').then(() => {
      if (this.#carousel !== carousel) return; // torn down (or re-initialized) meanwhile
      callback(carousel);
    });
  }

  /**
   * Locks the synced carousel's own prev/next buttons (rendered inside `render
   * 'slideshow'`, sections/collection-reel.liquid) while this stack is mid-flight.
   * Without this, clicking them rapidly fires more `slideshow:select` events than
   * `#handleCarouselSelect` can act on — its own `#isAnimating` guard just drops the
   * ones that land mid-animation rather than queuing them, permanently desyncing the
   * carousel's index from the stack's. A dedicated `data-stack-busy` attribute, not the
   * carousel's native `disabled` — that one is owned by `slideshow.js`'s own
   * edge-of-list logic (its `current` setter), which reruns on every `select()` call
   * (including this stack's own `#syncCarouselTo`) and would otherwise stomp on a lock
   * written directly to `disabled`.
   * @param {boolean} busy
   */
  #setCarouselArrowsBusy(busy) {
    this.#withCarousel((carousel) => {
      const refs = /** @type {any} */ (carousel.refs ?? {});
      refs.previous?.toggleAttribute('data-stack-busy', busy);
      refs.next?.toggleAttribute('data-stack-busy', busy);
    });
  }

  #destroy() {
    if (!this.#cards.length) return;

    this.#generation++;
    this.#isAnimating = false;
    this.#setCarouselArrowsBusy(false);
    this.#revealObserver?.disconnect();
    this.#revealObserver = null;
    this.#wrapper?.removeEventListener('pointerdown', this.#handlePointerDown);
    this.#wrapper?.removeEventListener('pointermove', this.#handlePointerMove);
    this.#wrapper?.removeEventListener('pointerup', this.#handlePointerUp);
    this.#wrapper?.removeEventListener('pointercancel', this.#handlePointerCancel);
    this.#wrapper?.removeEventListener('touchmove', this.#handleTouchMove);
    this.#wrapper = null;
    this.#carousel?.removeEventListener('slideshow:select', this.#handleCarouselSelect);
    this.#restoreCardsFromCarousel();

    for (const card of this.#cards) {
      card.classList.remove('exiting');
      card.style.removeProperty('transform');
      card.style.removeProperty('opacity');
      card.style.removeProperty('z-index');
      card.style.removeProperty('--rest-y');
      card.style.removeProperty('--rest-scale');
      card.style.removeProperty('--rest-opacity');
    }

    this.#cards = [];
    this.#carousel = null;
  }

  /**
   * `offsetHeight` reads a card's plain layout box, unaffected by any transform already
   * applied to it — reliable as the `cardHeight` input to `restStyleForDepth` regardless
   * of which depth that particular card currently happens to be resting at. Every card
   * shares the same crop/width, so any one of them gives the same answer.
   */
  #getCardHeight() {
    return this.#cards[0]?.offsetHeight ?? 0;
  }

  /** @param {number} active */
  #render(active) {
    const total = this.#cards.length;
    const cardHeight = this.#getCardHeight();
    this.#cards.forEach((card, i) => {
      const depth = depthOf(i, active, total);
      applyRest(card, depth, total, cardHeight);
      syncVideoPlayback(card, depth);
    });
  }

  /**
   * `#render()` stamps inline `transform`/`opacity` on every card — if the wrapper is a
   * still-unrevealed `data-reveal-group` (assets/motion.js, gated behind
   * `section.settings.scroll_animations`), those inline styles would permanently mask
   * motion.css's pre-reveal `opacity: 0`, and the entrance animation would silently never
   * play. assets/motion.js reveals by adding its one-shot `motion-revealed` class to each
   * card individually (not to the wrapper) the moment the group scrolls into view — this
   * waits for that on the first card (every card reveals together, as one group) before
   * rendering, and renders immediately when there's nothing to wait for at all: no reveal
   * group, already revealed (e.g. the section was already in view at load), or reduced
   * motion — assets/motion.js never adds the class in that last case either, so waiting
   * for it would mean never rendering at all.
   * @param {Element} wrapper
   */
  #renderOnceRevealed(wrapper) {
    const firstCard = this.#cards[0];
    const alreadySettled =
      !firstCard ||
      !wrapper.hasAttribute('data-reveal-group') ||
      prefersReducedMotion() ||
      firstCard.classList.contains('motion-revealed');

    if (!firstCard || alreadySettled) {
      this.#render(this.#activeIndex);
      this.#playSwipeHint();
      return;
    }

    this.#revealObserver = new MutationObserver(() => {
      if (!firstCard.classList.contains('motion-revealed')) return;
      this.#revealObserver?.disconnect();
      this.#revealObserver = null;
      this.#render(this.#activeIndex);
      this.#playSwipeHint();
    });
    this.#revealObserver.observe(firstCard, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * One-shot nudge-left-and-back on the active card, so a visitor who has never
   * dragged the deck can still tell it's swipeable in either direction. Scheduled
   * `SWIPE_HINT_DELAY_MS` after the stack's first render rather than immediately, so
   * it never overlaps the entrance fade/rise or this file's own resting `transition`
   * on the same `transform` property. `#generation` is captured up front and
   * rechecked when the timeout fires — a breakpoint flip back to desktop in between
   * must not stamp a class onto a card this instance no longer owns.
   */
  #playSwipeHint() {
    if (prefersReducedMotion()) return;

    const generation = this.#generation;
    setTimeout(() => {
      if (this.#generation !== generation) return;
      const card = this.#cards[this.#activeIndex];
      if (!card) return;
      card.addEventListener('animationend', () => card.classList.remove('swipe-hint'), { once: true });
      card.classList.add('swipe-hint');
    }, SWIPE_HINT_DELAY_MS);
  }

  /**
   * Drives the carousel to match a stack index the stack itself just moved to. Always
   * called with no source event, so `Slideshow#select` dispatches its own
   * `slideshow:select` with `userInitiated: false` — `#handleCarouselSelect` reads that
   * to tell this call apart from a real user interaction on the carousel and ignore it,
   * without needing a separate re-entrancy flag.
   * @param {number} index
   * @param {boolean} animate
   */
  #syncCarouselTo(index, animate) {
    this.#withCarousel((carousel) => carousel.select(index, undefined, { animate }));
  }

  /**
   * A user-initiated change on the carousel (drag or scroll-snap, not this component's
   * own `#syncCarouselTo` call). A step to either adjacent card — the only moves the
   * stack's own gesture ever makes — plays the exact same flick-and-recycle animation
   * as swiping the stack directly (`#advance`/`#retreat`), so the two interactions
   * never look different; anything further (an arbitrary jump) just re-renders the
   * stack at rest, no flourish.
   * @param {CustomEvent<{index: number, userInitiated: boolean}>} event
   */
  #handleCarouselSelect = (event) => {
    if (!event.detail?.userInitiated) return;
    if (this.#isAnimating) return;

    const { index } = event.detail;
    if (index === this.#activeIndex || index < 0 || index >= this.#cards.length) return;

    const total = this.#cards.length;
    if (index === (this.#activeIndex + 1) % total) {
      this.#animateAdvanceTo(index);
      return;
    }
    if (index === (this.#activeIndex - 1 + total) % total) {
      this.#animateRetreatTo(index);
      return;
    }

    this.#activeIndex = index;
    this.#renderSettling(index);
  };

  /**
   * @param {number} active
   */
  #renderSettling(active) {
    if (prefersReducedMotion()) {
      this.#render(active);
      return;
    }

    const total = this.#cards.length;
    const cardHeight = this.#getCardHeight();

    this.#isAnimating = true;
    this.#setCarouselArrowsBusy(true);
    const generation = this.#generation;
    let settled = false;
    let pending = this.#cards.length;
    const trySettle = () => {
      if (settled || pending > 0) return;
      settled = true;
      this.#isAnimating = false;
      this.#setCarouselArrowsBusy(false);
    };

    this.#cards.forEach((card, i) => {
      const depth = depthOf(i, active, total);
      applyRest(card, depth, total, cardHeight);
      syncVideoPlayback(card, depth);
      card.addEventListener(
        'transitionend',
        (event) => {
          if (event.target !== card || event.propertyName !== 'transform') return;
          pending--;
          trySettle();
        },
        { once: true },
      );
    });
    setTimeout(() => {
      if (this.#generation !== generation) return;
      pending = 0;
      trySettle();
    }, TRANSITION_MS + 100);
  }

  /**
   * Plays the flick-and-recycle exit animation on the current active card and settles
   * every other card at its new resting depth — the single choreography both the
   * stack's own forward swipe (`#advance`) and a forward step on the synced carousel
   * (`#handleCarouselSelect`) trigger, so they always animate identically. Updates
   * `#activeIndex` itself; does not touch the carousel (the caller decides whether it
   * still needs syncing).
   * @param {number} nextActive
   */
  #animateAdvanceTo(nextActive) {
    const total = this.#cards.length;
    const leavingIndex = this.#activeIndex;
    const leavingCard = /** @type {HTMLElement} */ (this.#cards[leavingIndex]);

    if (prefersReducedMotion()) {
      this.#activeIndex = nextActive;
      this.#render(nextActive);
      return;
    }

    this.#isAnimating = true;
    this.#setCarouselArrowsBusy(true);
    const generation = this.#generation;
    const cardHeight = this.#getCardHeight();

    // Other cards settle via their own CSS `transition`, a separate timeline from the
    // flicking card's `animation` — #isAnimating must wait for both to finish.
    let flickDone = false;
    let settled = false;
    const otherCards = this.#cards.filter((_, i) => i !== leavingIndex);
    let pendingSettles = otherCards.length;
    const trySettle = () => {
      if (settled || !flickDone || pendingSettles > 0) return;
      settled = true;
      this.#isAnimating = false;
      this.#setCarouselArrowsBusy(false);
    };

    otherCards.forEach((card) => {
      const depth = depthOf(this.#cards.indexOf(card), nextActive, total);
      applyRest(card, depth, total, cardHeight);
      syncVideoPlayback(card, depth);
      card.addEventListener(
        'transitionend',
        (event) => {
          if (event.target !== card || event.propertyName !== 'transform') return;
          pendingSettles--;
          trySettle();
        },
        { once: true },
      );
    });
    // Safety net in case a transitionend is ever missed (backgrounded tab, browser quirk).
    setTimeout(() => {
      if (this.#generation !== generation) return;
      pendingSettles = 0;
      trySettle();
    }, TRANSITION_MS + 100);

    syncVideoPlayback(leavingCard, depthOf(leavingIndex, nextActive, total));

    const restAtBack = restStyleForDepth(depthOf(leavingIndex, nextActive, total), total, cardHeight);
    leavingCard.style.setProperty('--rest-y', `${restAtBack.y}px`);
    leavingCard.style.setProperty('--rest-scale', String(restAtBack.scale));
    leavingCard.style.setProperty('--rest-opacity', String(restAtBack.opacity));

    // Front of the deck during the flick — one above the highest resting z-index
    // (`total`, the active card's own), so it stays above every other card while it
    // slides out, regardless of how many cards are in the deck.
    leavingCard.style.zIndex = String(total + 1);
    leavingCard.classList.add('exiting');

    const reversalTimeout = setTimeout(() => {
      if (this.#generation !== generation) return;
      leavingCard.style.zIndex = String(restAtBack.zIndex);
    }, TRANSITION_MS * EXIT_REVERSAL_FRACTION);

    leavingCard.addEventListener(
      'animationend',
      () => {
        clearTimeout(reversalTimeout);
        if (this.#generation !== generation) return;

        leavingCard.classList.remove('exiting');
        leavingCard.style.removeProperty('--rest-y');
        leavingCard.style.removeProperty('--rest-scale');
        leavingCard.style.removeProperty('--rest-opacity');
        applyRest(leavingCard, depthOf(leavingIndex, nextActive, total), total, cardHeight);
        flickDone = true;
        trySettle();
      },
      { once: true },
    );

    this.#activeIndex = nextActive;
  }

  #advance() {
    if (this.#isAnimating || this.#cards.length < 2) return;

    const reduced = prefersReducedMotion();
    const nextActive = (this.#activeIndex + 1) % this.#cards.length;

    this.#animateAdvanceTo(nextActive);
    this.#syncCarouselTo(nextActive, !reduced);
  }

  /**
   * The literal time-reversal of `#animateAdvanceTo`: the card already resting at the
   * back of the deck (depth total-1 — exactly where a previous forward step would have
   * sent it) flies back to the front. Plays the same `collection-reel-image-exit`
   * keyframes as the forward flick, just in reverse (`.entering`, section stylesheet) —
   * at reverse-time 0 it's already sitting at its current back pose (the vars below
   * pin that down), so there's no jump, and at reverse-time 100% it lands on the
   * front's identity transform, exactly depth 0. Every other card just settles at its
   * new (one deeper) resting depth via the plain CSS transition, same as
   * `#animateAdvanceTo`. Updates `#activeIndex` itself; does not touch the carousel.
   * @param {number} nextActive
   */
  #animateRetreatTo(nextActive) {
    const total = this.#cards.length;
    const previousActive = this.#activeIndex;
    const enteringCard = /** @type {HTMLElement} */ (this.#cards[nextActive]);

    if (prefersReducedMotion()) {
      this.#activeIndex = nextActive;
      this.#render(nextActive);
      return;
    }

    this.#isAnimating = true;
    this.#setCarouselArrowsBusy(true);
    const generation = this.#generation;
    const cardHeight = this.#getCardHeight();

    // See #animateAdvanceTo's matching comment — #isAnimating must wait for every other
    // card's own CSS `transition` to settle too, not just the entering card's keyframe
    // `animation`.
    let flightDone = false;
    let settled = false;
    const otherCards = this.#cards.filter((_, i) => i !== nextActive);
    let pendingSettles = otherCards.length;
    const trySettle = () => {
      if (settled || !flightDone || pendingSettles > 0) return;
      settled = true;
      this.#isAnimating = false;
      this.#setCarouselArrowsBusy(false);
    };

    otherCards.forEach((card) => {
      const depth = depthOf(this.#cards.indexOf(card), nextActive, total);
      applyRest(card, depth, total, cardHeight);
      syncVideoPlayback(card, depth);
      card.addEventListener(
        'transitionend',
        (event) => {
          if (event.target !== card || event.propertyName !== 'transform') return;
          pendingSettles--;
          trySettle();
        },
        { once: true },
      );
    });
    setTimeout(() => {
      if (this.#generation !== generation) return;
      pendingSettles = 0;
      trySettle();
    }, TRANSITION_MS + 100);

    syncVideoPlayback(enteringCard, 0);

    // The entering card's current (pre-old-active) resting pose is the flight's start
    // point — the reverse keyframes read it back out of these same custom properties.
    const startDepth = depthOf(nextActive, previousActive, total);
    const startRest = restStyleForDepth(startDepth, total, cardHeight);
    enteringCard.style.setProperty('--rest-y', `${startRest.y}px`);
    enteringCard.style.setProperty('--rest-scale', String(startRest.scale));
    enteringCard.style.setProperty('--rest-opacity', String(startRest.opacity));

    // Unlike the leaving card in #animateAdvanceTo (already active, so already on top),
    // the entering card starts buried behind every other card. Elevating it only partway
    // through the flight left the first half occluded, so it seemed to snap into view
    // mid-flourish — elevating immediately keeps the whole flight visible.
    enteringCard.style.zIndex = String(total + 1);
    enteringCard.classList.add('entering');

    enteringCard.addEventListener(
      'animationend',
      () => {
        if (this.#generation !== generation) return;

        enteringCard.classList.remove('entering');
        enteringCard.style.removeProperty('--rest-y');
        enteringCard.style.removeProperty('--rest-scale');
        enteringCard.style.removeProperty('--rest-opacity');
        applyRest(enteringCard, 0, total, cardHeight);
        flightDone = true;
        trySettle();
      },
      { once: true },
    );

    this.#activeIndex = nextActive;
  }

  #retreat() {
    if (this.#isAnimating || this.#cards.length < 2) return;

    const reduced = prefersReducedMotion();
    const total = this.#cards.length;
    const nextActive = (this.#activeIndex - 1 + total) % total;

    this.#animateRetreatTo(nextActive);
    this.#syncCarouselTo(nextActive, !reduced);
  }

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (!event.isPrimary) return;
    // A real gesture starting supersedes the hint outright, whether or not it's
    // still mid-animation — avoids the class fighting `.exiting`'s own `animation`
    // if `#advance()`/`#retreat()` run on this same (active) card before the hint
    // finishes.
    this.#cards[this.#activeIndex]?.classList.remove('swipe-hint');
    this.#ignoringGesture = this.#isAnimating;
    if (this.#ignoringGesture) return;
    this.#pointerDownX = event.clientX;
    this.#pointerDownY = event.clientY;
    this.#dragDirection = null;
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!event.isPrimary || this.#ignoringGesture) return;
    if (this.#dragDirection !== null) return;

    const dx = event.clientX - this.#pointerDownX;
    const dy = event.clientY - this.#pointerDownY;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < DIRECTION_LOCK_MOVE_PX) return;
    this.#dragDirection = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
  };

  /**
   * Cancels the section's native `touch-action: pan-y` scroll for gestures
   * `#handlePointerMove` locked "horizontal" — a real `TouchEvent`, needed on iOS Safari
   * specifically (see `assets/product-editorial.js`'s identical handler).
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (this.#dragDirection === 'horizontal') event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!event.isPrimary) return;
    if (this.#ignoringGesture) {
      this.#ignoringGesture = false;
      this.#dragDirection = null;
      return;
    }

    const dx = event.clientX - this.#pointerDownX;
    const dy = event.clientY - this.#pointerDownY;

    // A drag that's more vertical than horizontal is left alone so the page can
    // scroll; otherwise a clear leftward drag advances (spec §4's forward swipe) and a
    // clear rightward one retreats, each moving exactly one card.
    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < -SWIPE_THRESHOLD_PX) this.#advance();
      else if (dx > SWIPE_THRESHOLD_PX) this.#retreat();
    }
    this.#dragDirection = null;
  };

  /** @param {PointerEvent} event */
  #handlePointerCancel = (event) => {
    if (!event.isPrimary) return;
    this.#ignoringGesture = false;
    this.#dragDirection = null;
  };
}

if (!customElements.get('collection-reel-component')) {
  customElements.define('collection-reel-component', CollectionReelComponent);
}
